import * as React from 'react';

import {
  enumCurrencyCode,
  enumUnit,
  MoneyInput,
  Unit,
  Weight,
} from '@rematter/frontend-lib-api-client';

import { ItemMarginRecord } from '~/modules/brokerage/types/brokeredOrder';
import { createMarginsMap } from '~/modules/brokerage/utils/lineItemCalculations';
import {
  calculateTotalEachCount,
  calculateTotalNetWeight,
  getCommonPricingUnit,
  LineItemForTotals,
} from '~/modules/trade/utils/weight';

type LineItemWithPricing = {
  itemId?: string;
  quantity: number;
  unit: Unit;
  purchaseUnitPrice?: MoneyInput | null;
  salesUnitPrice?: MoneyInput | null;
};

type BrokeredOrderLineItemsTotals = {
  totalWeight: Weight | null;
  totalEachCount: number;
  displayUnit: Unit;
  purchaseTotal: MoneyInput;
  salesTotal: MoneyInput;
  margin: MoneyInput;
  marginPercent: number;
};

export const useBrokeredOrderLineItemsTotals = (
  lineItems: LineItemWithPricing[],
  marginsData?: ItemMarginRecord[],
): BrokeredOrderLineItemsTotals =>
  React.useMemo(() => {
    const items: LineItemForTotals[] = lineItems.map((li) => ({
      quantity: li.quantity,
      unit: li.unit,
    }));

    const displayUnit = getCommonPricingUnit(items, enumUnit.Pounds);
    const totalWeight = calculateTotalNetWeight(items, displayUnit);
    const totalEachCount = calculateTotalEachCount(items);

    const marginsMap = marginsData ? createMarginsMap(marginsData) : null;

    const resolvedPrices = lineItems.map((li) => {
      const md = marginsMap?.get(li.itemId ?? '');
      return {
        purchaseUnitPrice: md?.purchaseUnitPrice ?? li.purchaseUnitPrice,
        salesUnitPrice: md?.salesUnitPrice ?? li.salesUnitPrice,
        quantity: li.quantity,
      };
    });

    const purchaseTotalAmount = resolvedPrices.reduce(
      (sum, p) => sum + (p.purchaseUnitPrice?.amount ?? 0) * p.quantity,
      0,
    );

    const salesTotalAmount = resolvedPrices.reduce(
      (sum, p) => sum + (p.salesUnitPrice?.amount ?? 0) * p.quantity,
      0,
    );

    const currencyCode =
      resolvedPrices.find((p) => p.purchaseUnitPrice?.currencyCode)
        ?.purchaseUnitPrice?.currencyCode ?? enumCurrencyCode.USD;

    const marginAmount = salesTotalAmount - purchaseTotalAmount;
    const marginPercent =
      salesTotalAmount > 0 ? (marginAmount / salesTotalAmount) * 100 : 0;

    return {
      totalWeight,
      totalEachCount,
      displayUnit,
      purchaseTotal: { amount: purchaseTotalAmount, currencyCode },
      salesTotal: { amount: salesTotalAmount, currencyCode },
      margin: { amount: marginAmount, currencyCode },
      marginPercent,
    };
  }, [lineItems, marginsData]);
